import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";

// A second of quiet after the last keystroke, as in the match editor.
const QUIET_MS = 1000;

// The notebook's whole text as plain Markdown, for changes the chain of passages cannot make in place. Saved
// the way the match editor saves (@adr SPARK-0048-studio-notebook-saves-like-the-match-editor@2): shortly after typing stops, and at
// once on "Done" or when the editor is left another way. There is no discard: what is typed is the notebook's.
export function StudioNotebookEditor({ text, disabled, onSave, onDone }: {
  text: string;
  disabled: boolean;
  onSave: (text: string) => Promise<boolean>;
  onDone: () => void;
}) {
  const intl = useIntl();
  const [draft, setDraft] = useState(text);
  const pending = useRef<{ timer: ReturnType<typeof setTimeout>; value: string } | null>(null);
  const saved = useRef(text.trim());
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const flush = useCallback(() => {
    const next = pending.current;
    if (!next) return;
    clearTimeout(next.timer);
    pending.current = null;
    if (next.value.trim() === saved.current) return;
    saved.current = next.value.trim();
    void onSaveRef.current(next.value);
  }, []);
  // Leaving the editor (another notebook, the list) still saves what was typed.
  useEffect(() => flush, [flush]);

  const change = (value: string) => {
    setDraft(value);
    if (pending.current) clearTimeout(pending.current.timer);
    pending.current = { value, timer: setTimeout(flush, QUIET_MS) };
  };

  return <div className="box max-w-4xl bg-surface p-5">
    <textarea
      id="studio-text"
      autoFocus
      aria-label={intl.formatMessage({ id: "studio.text", defaultMessage: "Notebook text" })}
      value={draft}
      disabled={disabled}
      onChange={(event) => change(event.target.value)}
      rows={16}
      placeholder={intl.formatMessage({ id: "studio.notePlaceholder", defaultMessage: "Paste names, links, search results or your own thoughts here…" })}
      className="smycles-field-value w-full resize-y rounded-md bg-page p-3 ring-1 ring-ink/10 placeholder:text-ink-muted focus:outline-none focus:ring-ink-ui"
    />
    <div className="mt-3 flex justify-end">
      <Button id="studio-text-done" variant="primary" onClick={() => { flush(); onDone(); }}>
        <FormattedMessage id="studio.textDone" defaultMessage="Done" />
      </Button>
    </div>
  </div>;
}
